// Verkennen "eenvoudig" (editie 11) — de rekenregels en de gids-teksten.
//
// Ontwerp: docs/decisions/2026-09-10-verkennen-editie-11-eenvoudig.md (en editie
// 9/10 ervoor). Het prototype (docs/input/frontend/verkennen-editie-5/
// editie11.app.js + bouw11.py) rekende op dumps van 4 september; hier rekent
// hetzelfde op de echte API: /api/graaf/kaart (punten, lijnen, thema's) en
// /api/graaf/rapporten (de rapporten achter de onderwerpen).
//
// Alles hier is puur (geen DOM, geen React), zodat de regels per onderdeel te
// toetsen zijn: de wereld op vaste plekken, de knoppen per afstand, de gids,
// Bouwen en de stoplichten.

import type { GraafKaartResponse, GraafPunt, GraafRapportenResponse, GraafSkillkaartResponse } from "../contracts";
import { chart } from "../tokens";
import { maandNr, maandTekst } from "./verkennen";

/** De bewijslaag van 01-10 (weging v3): alleen trends dragen hem. */
export function isBevestigd(p: Pick<GraafPunt, "bewijs"> | null | undefined): boolean {
  return p?.bewijs?.laag === "bevestigd";
}

// ── woorden en kleuren ─────────────────────────────────────────────────────

export const SECTOR_NAAM: Record<string, string> = {
  ict: "ICT", zorg: "Zorg", maakindustrie: "Maakindustrie", hightech: "Hightech", zakelijk: "Zakelijke dienstverlening", publiek: "Publieke sector",
};
export function sectorNaam(s: string): string {
  return SECTOR_NAAM[s] ?? s.charAt(0).toUpperCase() + s.slice(1);
}

/** Kleur per gebied, in de volgorde van config/taxonomy/themas.yaml (paars, rood, blauw, cyaan, amber, groen). */
const GEBIED_KLEUREN = [chart.purple, chart.red, chart.blue, chart.cyan, chart.amber, chart.green, chart.teal, "#ec4899", "#6366f1", "#84cc16"];
export function gebiedKleur(i: number, naam: string): string {
  if (naam === "Overig") return chart.slate;
  return GEBIED_KLEUREN[i % GEBIED_KLEUREN.length]!;
}

export type Preset = "nieuw" | "groei" | "samen" | "student" | "wie" | "gat" | "speel";
export const PRESET_NAAM: Record<Preset, string> = {
  nieuw: "Wat is nieuw dit jaar", groei: "Wat groeit het hardst", samen: "Wat hoort bij elkaar", student: "Wat moet een student kunnen",
  wie: "Wie zegt dat?", gat: "Waar zit een gat", speel: "Speel het jaar af",
};
export type Stand = 1 | 2 | 3;
/** Hooguit drie knoppen per afstand (B1); de rest onder "meer". */
export const KNOPPEN: Record<Stand, Preset[]> = { 1: ["nieuw", "samen", "gat"], 2: ["nieuw", "groei", "samen"], 3: ["student", "nieuw", "wie"] };
export const MEER: Record<Stand, Preset[]> = { 1: ["speel"], 2: ["gat", "speel"], 3: ["speel"] };
export type Doel = "nieuw" | "student" | "bouw" | null;

export function nl(n: number): string { return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, "."); }
export function pct(x: number): string { return Math.round(x * 100) + "%"; }
export function komma(x: number): string { return x.toFixed(1).replace(".", ","); }
export function mv(n: number, een: string, meer: string): string { return n + " " + (n === 1 ? een : meer); }
export function kort(s: string, n: number): string { return s.length > n ? s.slice(0, n - 1) + "…" : s; }
export function clamp(v: number, a: number, b: number): number { return v < a ? a : v > b ? b : v; }
const MND = ["januari", "februari", "maart", "april", "mei", "juni", "juli", "augustus", "september", "oktober", "november", "december"];
export function maandLang(nr: number): string { const j = Math.floor((nr - 1) / 12); return MND[(nr - 1) % 12] + " " + j; }
export function datumLang(iso: string | null | undefined): string {
  if (!iso) return "";
  const n = maandNr(iso.slice(0, 7));
  return n === null ? iso : maandLang(n);
}
export function lijstNamen(a: string[]): string { return a.length < 3 ? a.join(" en ") : a.slice(0, -1).join(", ") + " en " + a[a.length - 1]; }

// ── de wereld ──────────────────────────────────────────────────────────────

export type Gebied = {
  i: number; naam: string; kleur: string; ow: number[];
  docs: number; punten: number; nieuw: number; aandeel: number; dun: boolean;
  docSet: Set<number>; maand: number[]; basis: number; cum: number[];
  x: number; y: number; r: number;
};
export type Onderwerp = {
  i: number; id: string; naam: string; t: number; kleur: string;
  /** hoofd- of deelonderwerp (baan C); zonder die velden: hoofd */
  niveau: "hoofd" | "deel"; hoofd: string | null; opkomend: boolean;
  docs: number; trends: number; skills: number; punten: number; nieuw: number; aandeel: number;
  bevestigd: number; mom: number; band: "dun" | "versnelt" | "groeit" | "stabiel" | "stil"; dun: boolean;
  docSet: Set<number>; skillSet: Set<string>; maand: number[]; basis: number; cum: number[];
  sk: number[]; x: number; y: number; r: number;
};
export type SkillBol = {
  i: number; id: string; ow: number; naam: string;
  /** gevraagd door zoveel ontwikkelingen van dit onderwerp; nw = waarvan van het laatste jaar; nb = waarvan bevestigd */
  n: number; nw: number; nb: number; dub: number; van: number | null;
  x: number; y: number; r: number;
};
export type Wereld = {
  sector: string; nu: number; maanden: number[];
  gebieden: Gebied[]; onderwerpen: Onderwerp[]; skills: SkillBol[];
  veld: { docs: number; rec: number }; metRapporten: boolean; aantalBevestigd: number;
  punt: Map<string, GraafPunt>;
  /** per skill-id: de trends (ids) die haar vragen (brug-lijnen) */
  vragers: Map<string, string[]>;
  /** per skill-id: breder/smaller-buren die skill zijn */
  lijkt: Map<string, string[]>;
  totaalRapporten: number;
};

type OnderwerpExtra = { niveau?: string | null; hoofd?: string | null; ouder?: string | null; hoofdonderwerp?: string | null; status?: string | null; opkomend?: boolean | null };

/** Hoofd- of deelonderwerp en opkomend lezen van een onderwerppunt. De velden
 *  komen uit baan C (onderwerpen per sector); ontbreken ze, dan is alles een
 *  vast hoofdonderwerp — precies de kaart van nu. */
export function onderwerpSoort(p: GraafPunt): { niveau: "hoofd" | "deel"; hoofd: string | null; opkomend: boolean } {
  const e = p as GraafPunt & OnderwerpExtra;
  const hoofd = e.hoofd ?? e.hoofdonderwerp ?? e.ouder ?? null;
  const niveau = e.niveau === "deel" || (e.niveau == null && !!hoofd && hoofd !== p.naam) ? "deel" : "hoofd";
  const opkomend = e.opkomend === true || e.status === "opkomend";
  return { niveau, hoofd: niveau === "deel" ? hoofd : null, opkomend };
}

/** Nu = de laatste VOLLE maand vóór de peildag. */
export function nuVan(peil: string | null | undefined, kaart: GraafKaartResponse): number {
  if (peil) {
    const n = maandNr(peil.slice(0, 7));
    if (n !== null) return n - 1;
  }
  let nu = 0;
  for (const p of kaart.punten) for (const m of p.bewijsMaanden ?? []) { const n = maandNr(m); if (n !== null && n > nu) nu = n; }
  return nu || 2026 * 12 + 9;
}

// Vaste plekken (bouw10.py/bouw11.py): cirkels gepakt rond het midden.
export function pack(radii: number[], gap: number, ax = 1): [number, number][] {
  const order = radii.map((_, i) => i).sort((a, b) => radii[b]! - radii[a]! || a - b);
  const placed: [number, number, number][] = [];
  const pos = new Map<number, [number, number]>();
  for (const i of order) {
    const r = radii[i]!;
    if (!placed.length) { pos.set(i, [0, 0]); placed.push([0, 0, r]); continue; }
    let best: [number, number, number] | null = null;
    for (const [x, y, rr] of placed) {
      for (let a = 0; a < 360; a += 3) {
        const t = (a * Math.PI) / 180, dd = rr + r + gap;
        const cx = x + dd * Math.cos(t), cy = y + dd * Math.sin(t);
        if (placed.every(([px, py, pr]) => Math.hypot(cx - px, cy - py) >= pr + r + gap - 1e-6)) {
          const cost = Math.hypot(cx / ax, cy);
          if (!best || cost < best[0] - 1e-9) best = [cost, cx, cy];
        }
      }
    }
    const b = best ?? [0, 0, 0];
    pos.set(i, [b[1], b[2]]); placed.push([b[1], b[2], r]);
  }
  let x0 = Infinity, x1 = -Infinity, y0 = Infinity, y1 = -Infinity;
  for (const [i, [x, y]] of pos) { const r = radii[i]!; x0 = Math.min(x0, x - r); x1 = Math.max(x1, x + r); y0 = Math.min(y0, y - r); y1 = Math.max(y1, y + r); }
  const mx = (x0 + x1) / 2, my = (y0 + y1) / 2;
  return radii.map((_, i) => { const p = pos.get(i)!; return [p[0] - mx, p[1] - my]; });
}
export function inpassen(c: [number, number], R: number, radii: number[], gap: number, ax: number, vul = 0.8, minr = 0): [number, number, number][] {
  if (!radii.length) return [];
  const pr = radii.map((r) => Math.max(r, minr));
  const p = pack(pr, gap, ax);
  const enc = Math.max(...p.map(([x, y], i) => Math.hypot(x, y) + pr[i]!)) || 1;
  const s = (vul * R) / enc;
  return p.map(([x, y], i) => [c[0] + s * x, c[1] + s * y, s * radii[i]!]);
}

export function bouwWereld(kaart: GraafKaartResponse, rap: GraafRapportenResponse | null, sector: string): Wereld {
  const nu = nuVan(rap?.peil, kaart);
  const begin = 2024 * 12 + 1;
  const maanden: number[] = [];
  for (let m = Math.min(begin, nu); m <= nu; m++) maanden.push(m);
  const punten = kaart.punten;
  const punt = new Map<string, GraafPunt>();
  for (const p of punten) punt.set(p.id, p);
  const eerste = (p: GraafPunt | undefined) => (p ? maandNr(p.eersteBewijs) : null);
  const isNieuw = (p: GraafPunt | undefined) => { const e = eerste(p); return e !== null && e >= nu - 11; };

  // Leden per onderwerp en de brug trend ↔ skill.
  const leden = new Map<string, string[]>();
  const vragers = new Map<string, string[]>();
  const lijkt = new Map<string, string[]>();
  for (const l of kaart.lijnen) {
    const a = punten[l.van], b = punten[l.naar];
    if (!a || !b) continue;
    if (l.soort === "onderwerp") {
      const ow = b.soort === "onderwerp" ? b : a, lid = ow === b ? a : b;
      const lst = leden.get(ow.id) ?? []; lst.push(lid.id); leden.set(ow.id, lst);
    } else if (l.soort === "bron") {
      const t = a.soort === "trend" ? a : b.soort === "trend" ? b : null;
      const s = a.soort === "vaardigheid" ? a : b.soort === "vaardigheid" ? b : null;
      if (t && s) { const lst = vragers.get(s.id) ?? []; lst.push(t.id); vragers.set(s.id, lst); }
    } else if (l.soort === "breder_smaller" && a.soort === "vaardigheid" && b.soort === "vaardigheid") {
      for (const [x, y] of [[a, b], [b, a]] as const) { const lst = lijkt.get(x.id) ?? []; if (!lst.includes(y.id)) lst.push(y.id); lijkt.set(x.id, lst); }
    }
  }

  // Rapporten.
  const metRapporten = !!rap && rap.rapporten.length > 0;
  const rapMaand = (rap?.rapporten ?? []).map((r) => maandNr(r.maand));
  const docsVan = new Map<string, number[]>();
  for (const o of rap?.onderwerpen ?? []) docsVan.set(o.naam, o.rapporten);
  const C6 = rapMaand.filter((m) => m !== null && m >= nu - 5 && m <= nu).length;
  const C18 = rapMaand.filter((m) => m !== null && m >= nu - 17 && m <= nu - 6).length;
  const reeks = (docs: Iterable<number>) => {
    const maand = new Array<number>(maanden.length).fill(0); let tot = 0;
    for (const d of docs) { tot++; const m = rapMaand[d]; if (m !== null && m !== undefined && m >= maanden[0]! && m <= nu) maand[m - maanden[0]!]!++; }
    const basis = tot - maand.reduce((a, b) => a + b, 0);
    const cum: number[] = []; let c = basis; for (const n of maand) { c += n; cum.push(c); }
    return { maand, basis, cum };
  };

  // Gebieden uit de thema's (K4); een onderwerp zonder thema volgt zijn hoofdonderwerp, anders "Overig".
  const owPunten = punten.filter((p) => p.soort === "onderwerp" && (leden.get(p.id)?.length ?? 0) > 0);
  const themaVanNaam = new Map<string, string>();
  for (const t of kaart.themas ?? []) for (const n of t.onderwerpen) themaVanNaam.set(n, t.naam);
  const themaNamen = (kaart.themas ?? []).map((t) => t.naam);
  const gebiedVan = (p: GraafPunt): string => {
    const g = themaVanNaam.get(p.naam);
    if (g) return g;
    const s = onderwerpSoort(p);
    if (s.hoofd && themaVanNaam.get(s.hoofd)) return themaVanNaam.get(s.hoofd)!;
    return "Overig";
  };
  const gebiedNamen = [...themaNamen];
  if (owPunten.some((p) => gebiedVan(p) === "Overig") && !gebiedNamen.includes("Overig")) gebiedNamen.push("Overig");

  const onderwerpen: Onderwerp[] = [];
  const gebieden: Gebied[] = [];
  gebiedNamen.forEach((naam) => {
    const ows = owPunten.filter((p) => gebiedVan(p) === naam);
    if (!ows.length) return;
    const gi = gebieden.length;
    const kleur = gebiedKleur(themaNamen.indexOf(naam) >= 0 ? themaNamen.indexOf(naam) : gi, naam);
    const g: Gebied = { i: gi, naam, kleur, ow: [], docs: 0, punten: 0, nieuw: 0, aandeel: 0, dun: false, docSet: new Set(), maand: [], basis: 0, cum: [], x: 0, y: 0, r: 0 };
    const gLeden = new Set<string>();
    const gemaakt: Onderwerp[] = [];
    for (const p of ows) {
      const lid = leden.get(p.id) ?? [];
      const ls = lid.map((id) => punt.get(id)).filter((x): x is GraafPunt => !!x);
      const trends = ls.filter((x) => x.soort === "trend");
      const skills = ls.filter((x) => x.soort === "vaardigheid");
      const docSet = new Set<number>(docsVan.get(p.naam) ?? []);
      const r6 = [...docSet].filter((d) => { const m = rapMaand[d]; return m != null && m >= nu - 5 && m <= nu; }).length;
      const r618 = [...docSet].filter((d) => { const m = rapMaand[d]; return m != null && m >= nu - 17 && m <= nu - 6; }).length;
      const mom = r618 && C6 && C18 ? (r6 / C6) / (r618 / C18) : r6 ? 9 : 1;
      const s = onderwerpSoort(p);
      const nieuw = ls.filter(isNieuw).length;
      const docs = metRapporten ? docSet.size : ls.length;
      const dun = docs < 15;
      const o: Onderwerp = {
        i: -1, id: p.id, naam: p.naam, t: gi, kleur, niveau: s.niveau, hoofd: s.hoofd, opkomend: s.opkomend,
        docs, trends: trends.length, skills: skills.length, punten: ls.length, nieuw, aandeel: ls.length ? nieuw / ls.length : 0,
        bevestigd: trends.filter((t) => isBevestigd(t)).length,
        mom, band: dun ? "dun" : mom >= 1.5 ? "versnelt" : mom >= 1.1 ? "groeit" : mom >= 0.8 ? "stabiel" : "stil", dun,
        docSet, skillSet: new Set(skills.map((x) => x.id)), ...reeks(docSet), sk: [], x: 0, y: 0, r: 0,
      };
      ls.forEach((x) => gLeden.add(x.id));
      docSet.forEach((d) => g.docSet.add(d));
      gemaakt.push(o);
    }
    // Volgorde binnen het gebied: hoofdonderwerpen op grootte, een deelonderwerp direct na zijn hoofd.
    gemaakt.sort((a, b) => b.docs - a.docs || a.naam.localeCompare(b.naam));
    const volg: Onderwerp[] = [];
    for (const o of gemaakt.filter((x) => x.niveau === "hoofd")) { volg.push(o); for (const d of gemaakt.filter((x) => x.niveau === "deel" && x.hoofd === o.naam)) volg.push(d); }
    for (const o of gemaakt) if (!volg.includes(o)) volg.push(o);
    for (const o of volg) { o.i = onderwerpen.length; onderwerpen.push(o); g.ow.push(o.i); }
    g.punten = gLeden.size;
    g.nieuw = [...gLeden].filter((id) => isNieuw(punt.get(id))).length;
    g.aandeel = g.punten ? g.nieuw / g.punten : 0;
    g.docs = metRapporten ? g.docSet.size : g.punten;
    g.dun = g.docs < 15;
    Object.assign(g, reeks(g.docSet));
    gebieden.push(g);
  });

  // Kernskills per onderwerp: gevraagd door de meeste ontwikkelingen van dat onderwerp; zelfde naam = één bol (W3).
  const skills: SkillBol[] = [];
  for (const o of onderwerpen) {
    const owTrends = new Set((leden.get(o.id) ?? []).filter((id) => punt.get(id)?.soort === "trend"));
    const tel = new Map<string, { n: number; nw: number; nb: number }>();
    for (const [sid, ts] of vragers) {
      let n = 0, nw = 0, nb = 0;
      for (const t of new Set(ts)) if (owTrends.has(t)) { n++; const tp = punt.get(t); if (isNieuw(tp)) nw++; if (isBevestigd(tp)) nb++; }
      if (n) tel.set(sid, { n, nw, nb });
    }
    const rijen = [...tel.entries()].map(([sid, v]) => ({ sid, naam: punt.get(sid)?.naam ?? "", ...v }))
      .sort((a, b) => b.n - a.n || a.naam.localeCompare(b.naam));
    const gezien = new Map<string, { dub: number }>(), top: { sid: string; naam: string; n: number; nw: number; nb: number; dub: number }[] = [];
    for (const r of rijen) {
      const g = gezien.get(r.naam);
      if (g) { g.dub++; continue; }
      const rec = { ...r, dub: 0 }; gezien.set(r.naam, rec);
      if (top.length < 12) top.push(rec);
    }
    o.sk = top.map((t) => {
      const b: SkillBol = { i: skills.length, id: t.sid, ow: o.i, naam: t.naam, n: t.n, nw: t.nw, nb: t.nb, dub: t.dub, van: eerste(punt.get(t.sid)), x: 0, y: 0, r: 0 };
      skills.push(b); return b.i;
    });
  }

  // Vaste plekken.
  const RT = gebieden.map((g) => Math.max(18, 8 * Math.sqrt(Math.max(g.docs, 1))));
  const tp = pack(RT.map((r) => Math.max(r, 62)), 16, 1.7);
  gebieden.forEach((g, i) => { g.x = tp[i]![0]; g.y = tp[i]![1]; g.r = RT[i]!; });
  for (const g of gebieden) {
    const rr = g.ow.map((i) => Math.sqrt(Math.max(onderwerpen[i]!.docs, 1)));
    const mx = Math.max(...rr, 1);
    inpassen([g.x, g.y], g.r, rr, 0.3 * mx, 1.25, 0.8, 0.68 * mx).forEach(([x, y, r], j) => { const o = onderwerpen[g.ow[j]!]!; o.x = x; o.y = y; o.r = r; });
  }
  for (const o of onderwerpen) {
    if (!o.sk.length) continue;
    const rr = o.sk.map((i) => Math.sqrt(skills[i]!.n));
    const mx = Math.max(...rr);
    inpassen([o.x, o.y], o.r, rr, 0.075 * mx, 1.3, 0.82).forEach(([x, y, r], j) => { const s = skills[o.sk[j]!]!; s.x = x; s.y = y; s.r = r; });
  }

  const veldRec = rapMaand.filter((m) => m !== null && m >= nu - 5 && m <= nu).length;
  return {
    sector, nu, maanden, gebieden, onderwerpen, skills, punt, vragers, lijkt, metRapporten,
    veld: { docs: rap?.rapporten.length ?? 0, rec: veldRec },
    aantalBevestigd: punten.filter((p) => isBevestigd(p)).length,
    totaalRapporten: rap?.rapporten.length ?? 0,
  };
}

// ── de regels achter de knoppen ────────────────────────────────────────────

export function gedeeld(w: Wereld, a: number, b: number): number {
  const A = w.onderwerpen[a]!.skillSet, B = w.onderwerpen[b]!.skillSet;
  let n = 0; for (const x of A) if (B.has(x)) n++;
  return n;
}
export function owVan(w: Wereld, g: Gebied): Onderwerp[] { return g.ow.map((i) => w.onderwerpen[i]!); }
export function kernVan(w: Wereld, o: Onderwerp): SkillBol[] { return o.sk.map((i) => w.skills[i]!); }
export function ringw(a: number): number { return clamp(1.5 + 10 * (a - 0.2), 1.5, 6); }
export function nieuwSkills(w: Wereld, o: Onderwerp): SkillBol[] {
  return kernVan(w, o).filter((s) => s.n >= 4).sort((a, b) => b.nw / b.n - a.nw / a.n || b.n - a.n).slice(0, 3);
}
export function groeiOw(w: Wereld, g: Gebied): Onderwerp[] {
  return owVan(w, g).filter((o) => !o.dun && o.mom >= 1.1).sort((a, b) => b.mom - a.mom).slice(0, 3);
}
export function parenIn(w: Wereld, g: Gebied): [Onderwerp, Onderwerp, number][] {
  const r: [Onderwerp, Onderwerp, number][] = [];
  g.ow.forEach((a, x) => g.ow.forEach((b, y) => { if (y > x) { const n = gedeeld(w, a, b); if (n >= 3) r.push([w.onderwerpen[a]!, w.onderwerpen[b]!, n]); } }));
  return r.sort((p, q) => q[2] - p[2]).slice(0, 3);
}
export function alleParen(w: Wereld): [number, number, number][] {
  const r: [number, number, number][] = [];
  for (let a = 0; a < w.onderwerpen.length; a++) for (let b = a + 1; b < w.onderwerpen.length; b++) { const n = gedeeld(w, a, b); if (n >= 3) r.push([a, b, n]); }
  return r.sort((p, q) => q[2] - p[2]);
}
export function gebiedParen(w: Wereld): [Gebied, Gebied, number][] {
  const sets = w.gebieden.map((g) => { const s = new Set<string>(); for (const i of g.ow) w.onderwerpen[i]!.skillSet.forEach((x) => s.add(x)); return s; });
  const r: [Gebied, Gebied, number][] = [];
  for (let a = 0; a < sets.length; a++) for (let b = a + 1; b < sets.length; b++) { let n = 0; for (const x of sets[a]!) if (sets[b]!.has(x)) n++; if (n) r.push([w.gebieden[a]!, w.gebieden[b]!, n]); }
  return r.sort((p, q) => q[2] - p[2]).slice(0, 3);
}
export function gatIn(w: Wereld, lijst: Onderwerp[]): [Onderwerp, Onderwerp, number] | null {
  const g = lijst.filter((o) => !o.dun).sort((a, b) => b.docs - a.docs).slice(0, 10);
  let best: [Onderwerp, Onderwerp, number] | null = null;
  for (let i = 0; i < g.length; i++) for (let j = i + 1; j < g.length; j++) {
    if (gedeeld(w, g[i]!.i, g[j]!.i) < 3) { const sc = Math.min(g[i]!.docs, g[j]!.docs); if (!best || sc > best[2]) best = [g[i]!, g[j]!, sc]; }
  }
  return best;
}
export function studentTop(w: Wereld, o: Onderwerp, alleenBevestigd = false) {
  const n = (s: SkillBol) => (alleenBevestigd ? s.nb : s.n);
  const l = kernVan(w, o).filter((s) => n(s) > 0).sort((a, b) => n(b) - n(a));
  let b = Math.min(4, l.length);
  while (b > 1 && b < l.length && n(l[b - 1]!) === n(l[b]!)) b--;
  const n5 = b < l.length ? n(l[b]!) : null;
  return { top: l.slice(0, b), gelijk: n5 === null ? [] : l.filter((s, j) => j >= b && n(s) === n5), n5, alles: l };
}
/** Het onderwerp plus de twee die er het meest mee delen (Bouwen vanuit een skill). */
export function partners(w: Wereld, i: number): number[] {
  return [i, ...w.onderwerpen.filter((o) => o.i !== i).map((o) => [o.i, gedeeld(w, i, o.i)] as const)
    .filter((x) => x[1] >= 3).sort((a, b) => b[1] - a[1]).slice(0, 2).map((x) => x[0])];
}
export function standaardPreset(s: Stand, doel: Doel): Preset | null {
  if (s === 3) return "student";
  return doel === "nieuw" ? "nieuw" : null;
}
export function geldig(p: Preset | null, s: Stand): boolean { return !!p && (KNOPPEN[s].includes(p) || MEER[s].includes(p)); }

export type Nadruk = {
  items: { soort: "g" | "o" | "s"; i: number; nr: number | null; w?: number; sel?: boolean }[];
  lijnen: { a: ["g" | "o", number]; b: ["g" | "o", number]; w: number; tekst: string; stip?: boolean; punten?: boolean }[];
};
export type Positie = { s: Stand; t: number; o: number | null };
/** Wat de kaart benadrukt (ringen, nummers, lijnen) bij een knop op een afstand. */
export function nadruk(w: Wereld, p: Preset | null, f: Positie, sel: number | null, alleenBevestigd = false): Nadruk {
  const uit: Nadruk = { items: [], lijnen: [] };
  if (sel !== null) { uit.items.push({ soort: "s", i: sel, nr: null, sel: true }); return uit; }
  const g = w.gebieden[f.t]!, o = f.o !== null ? w.onderwerpen[f.o]! : null;
  if (p === "nieuw") {
    if (f.s === 1) w.gebieden.filter((x) => !x.dun).sort((a, b) => b.aandeel - a.aandeel).slice(0, 3).forEach((x, j) => uit.items.push({ soort: "g", i: x.i, nr: j + 1, w: ringw(x.aandeel) }));
    else if (f.s === 2) owVan(w, g).filter((x) => !x.dun).sort((a, b) => b.aandeel - a.aandeel).slice(0, 3).forEach((x, j) => uit.items.push({ soort: "o", i: x.i, nr: j + 1, w: ringw(x.aandeel) }));
    else if (o) nieuwSkills(w, o).forEach((x, j) => uit.items.push({ soort: "s", i: x.i, nr: j + 1, w: ringw(x.nw / x.n) }));
  } else if (p === "groei" && f.s === 2) {
    groeiOw(w, g).forEach((x, j) => uit.items.push({ soort: "o", i: x.i, nr: j + 1, w: x.band === "versnelt" ? 5 : 3 }));
  } else if (p === "samen" && f.s < 3) {
    if (f.s === 1) { const pr = gebiedParen(w), mx = pr[0]?.[2] ?? 1; pr.forEach((q) => uit.lijnen.push({ a: ["g", q[0].i], b: ["g", q[1].i], w: 1.5 + (5 * q[2]) / mx, tekst: q[2] + " skills" })); }
    else { const pr = parenIn(w, g), mx = pr[0]?.[2] ?? 1; pr.forEach((q) => uit.lijnen.push({ a: ["o", q[0].i], b: ["o", q[1].i], w: 1.5 + (5 * q[2]) / mx, tekst: q[2] + " skills" })); }
  } else if (p === "gat" && f.s < 3) {
    const gat = gatIn(w, f.s === 1 ? w.onderwerpen : owVan(w, g));
    if (gat) uit.lijnen.push({ a: ["o", gat[0].i], b: ["o", gat[1].i], w: 2, tekst: "minder dan 3 gedeeld", stip: true, punten: f.s === 1 });
  } else if (p === "student" && f.s === 3 && o) {
    studentTop(w, o, alleenBevestigd).top.forEach((x, j) => uit.items.push({ soort: "s", i: x.i, nr: j + 1 }));
  }
  return uit;
}

// ── de gids ────────────────────────────────────────────────────────────────

export type Actie =
  | { soort: "gebied"; i: number }
  | { soort: "onderwerp"; i: number }
  | { soort: "skill"; i: number }
  | { soort: "bouw"; ids: number[] }
  | { soort: "kies"; p: Preset }
  | { soort: "chat"; vraag: string }
  | { soort: "speel" };
export type GidsTekst = {
  zin: string; rest?: string;
  lijst?: { nr: number | null; naam: string; waarde: string; actie: Actie }[];
  klein?: string[]; knop?: { tekst: string; actie: Actie };
};

function helft(a: number): string { return Math.round(a * 100) === 50 ? "de helft" : pct(a); }
function daarvan(nw: number, n: number): string {
  if (nw * 2 === n) return "de helft daarvan is";
  if (nw === 0) return "geen daarvan is";
  if (nw === 1) return "één daarvan is";
  return nw + " daarvan zijn";
}
function eenheid(w: Wereld): [string, string] { return w.metRapporten ? ["rapport", "rapporten"] : ["ontwikkeling of skill", "ontwikkelingen en skills"]; }

export function speelTekst(w: Wereld, f: Positie, loopt: boolean): GidsTekst {
  const obj = f.s === 3 && f.o !== null ? w.onderwerpen[f.o]! : f.s === 2 ? w.gebieden[f.t]! : null;
  const bron: (Gebied | Onderwerp)[] = obj ? [obj] : w.gebieden;
  let best: [Gebied | Onderwerp, number] | null = null, bn = -1;
  for (const o of bron) o.maand.forEach((n, j) => { if (n > bn) { bn = n; best = [o, w.maanden[j]!]; } });
  const b = best as [Gebied | Onderwerp, number] | null;
  return {
    zin: "Druk op ▶: de bollen groeien met hun rapporten, maand voor maand.",
    rest: b && bn > 0 ? "De drukste maand" + (obj ? " voor " + obj.naam : "") + " was " + maandLang(b[1]) + ": " + bn + " rapporten" + (obj ? "" : " in " + b[0].naam) + "." : undefined,
    knop: { tekst: loopt ? "❚❚ Pauze" : "▶ Speel het jaar af", actie: { soort: "speel" } },
  };
}

export function gidsTekst(w: Wereld, p: Preset | null, f: Positie, ctx: { doel: Doel; sel: number | null; loopt: boolean; alleenBevestigd: boolean; kaartje?: KaartjeCijfers | null }): GidsTekst {
  const [, veel] = eenheid(w);
  if (p === "speel") return speelTekst(w, f, ctx.loopt);
  const sector = sectorNaam(w.sector);
  if (f.s === 1) {
    const r = w.gebieden.filter((t) => !t.dun).sort((a, b) => b.aandeel - a.aandeel);
    if (!r.length) return { zin: "Er is nog te weinig om iets te laten zien.", rest: "Elk gebied heeft minder dan 15 " + veel + "." };
    if (p === "nieuw") {
      const t1 = r[0]!, rest = Math.max(0, ...r.slice(1).map((t) => t.aandeel));
      return {
        zin: "In " + t1.naam + " is het meeste nieuw.",
        rest: t1.nieuw + " van de " + nl(t1.punten) + " ontwikkelingen en skills kwamen er het laatste jaar bij." + (r.length > 1 ? " In de andere gebieden is dat hooguit " + pct(rest) + "." : ""),
        lijst: r.slice(0, 3).map((t, j) => ({ nr: j + 1, naam: t.naam, waarde: pct(t.aandeel) + " nieuw", actie: { soort: "gebied", i: t.i } })),
        knop: { tekst: "Zoom in op " + t1.naam, actie: { soort: "gebied", i: t1.i } },
      };
    }
    if (p === "samen") {
      const pr = gebiedParen(w), a = pr[0];
      if (!a) return { zin: "De gebieden delen (nog) geen skills." };
      return {
        zin: a[0].naam + " en " + a[1].naam + " horen het meest bij elkaar: " + a[2] + " skills gemeenschappelijk.",
        rest: "Wie het een leert, leert een deel van het ander mee.",
        lijst: pr.map((q) => ({ nr: null, naam: q[0].naam + " + " + q[1].naam, waarde: q[2] + " skills", actie: { soort: "gebied", i: q[0].i } })),
        knop: { tekst: "Zoom in op " + a[0].naam, actie: { soort: "gebied", i: a[0].i } },
      };
    }
    if (p === "gat") {
      const g = gatIn(w, w.onderwerpen);
      if (!g) return { zin: "Ik vind geen gat: de grote onderwerpen delen allemaal skills." };
      const vraag = "Wat verbindt " + g[0].naam + " en " + g[1].naam + "?";
      return { zin: g[0].naam + " en " + g[1].naam + " zijn allebei groot, maar delen bijna geen skills (minder dan 3).", rest: "Weet iemand wat ze verbindt? Dat is een vraag voor de chat.", knop: { tekst: "Stel de vraag in de chat", actie: { soort: "chat", vraag } } };
    }
    if (ctx.doel === "student") return { zin: "Kies een gebied, daarna een onderwerp.", rest: "Dan laat ik de skills zien die daar het vaakst terugkomen.", knop: { tekst: "Begin bij " + r[0]!.naam, actie: { soort: "gebied", i: r[0]!.i } } };
    return { zin: "Dit is het veld: " + w.gebieden.length + " gebieden in " + sector + ".", rest: "Klik een gebied om in te zoomen, of kies bovenaan wat je wilt zien.", knop: { tekst: "Wat is nieuw dit jaar?", actie: { soort: "kies", p: "nieuw" } } };
  }
  if (f.s === 2) {
    const T = w.gebieden[f.t]!, ows = owVan(w, T);
    const dun = ows.filter((o) => o.dun).map((o) => o.naam);
    const dunT = dun.length ? [kort(lijstNamen(dun), 160) + (dun.length === 1 ? " is" : " zijn") + " grijs: te weinig " + veel + " om iets te zeggen."] : [];
    const opk = ows.filter((o) => o.opkomend).map((o) => o.naam);
    const opkT = opk.length ? ["Met een stippelrand: " + lijstNamen(opk) + " — " + (opk.length === 1 ? "een opkomend onderwerp" : "opkomende onderwerpen") + ", nog niet vast."] : [];
    const klein = [...dunT, ...opkT];
    if (ows.length === 1) return { zin: T.naam + " heeft één onderwerp: " + ows[0]!.naam + ".", rest: ows[0]!.docs + " " + veel + ".", klein, knop: { tekst: "Zoom in op " + ows[0]!.naam, actie: { soort: "onderwerp", i: ows[0]!.i } } };
    if (p === "nieuw") {
      const r2 = ows.filter((o) => !o.dun).sort((a, b) => b.aandeel - a.aandeel), top = r2[0];
      if (!top) return { zin: "In " + T.naam + " is te weinig om iets nieuws te zien.", klein };
      let doel = top, rest2: string | undefined;
      let partner: Onderwerp | null = null, pg = 0;
      for (const o of w.onderwerpen) if (o !== top) { const g2 = gedeeld(w, top.i, o.i); if (g2 > pg) { pg = g2; partner = o; } }
      if (partner && top.skills && pg / top.skills >= 0.8 && partner.docs > top.docs) {
        rest2 = top.naam + " hoort bijna helemaal bij " + partner.naam + ": " + pg + " van zijn " + top.skills + " skills zitten ook daar. " + partner.naam + " is voor " + helft(partner.aandeel) + " nieuw, en veel groter.";
        doel = partner;
      } else rest2 = r2[1] ? "Daarna " + r2[1].naam + " (" + pct(r2[1].aandeel) + ")." : undefined;
      return {
        zin: "Het nieuwst in " + T.naam + " is " + top.naam + ": " + top.nieuw + " van de " + top.punten + " kwamen er het laatste jaar bij.",
        rest: rest2, lijst: r2.slice(0, 3).map((o, j) => ({ nr: j + 1, naam: o.naam, waarde: pct(o.aandeel) + " nieuw", actie: { soort: "onderwerp", i: o.i } })), klein,
        knop: { tekst: "Zoom in op " + doel.naam, actie: { soort: "onderwerp", i: doel.i } },
      };
    }
    if (p === "groei") {
      const gr = groeiOw(w, T);
      if (!gr.length) return { zin: "In " + T.naam + " groeit niets hard.", rest: "Alle onderwerpen krijgen ongeveer evenveel nieuwe rapporten als het jaar ervoor.", klein };
      const g1 = gr[0]!;
      return {
        zin: g1.naam + " groeit het hardst in " + T.naam + ".",
        rest: "In het laatste half jaar kwamen er naar verhouding " + komma(g1.mom) + " keer zoveel rapporten bij als in het jaar ervoor" + (g1.docs < 40 ? ", maar het gaat om weinig rapporten (" + g1.docs + ")." : "."),
        lijst: gr.map((o, j) => ({ nr: j + 1, naam: o.naam, waarde: komma(o.mom) + "×", actie: { soort: "onderwerp", i: o.i } })), klein,
        knop: { tekst: "Zoom in op " + g1.naam, actie: { soort: "onderwerp", i: g1.i } },
      };
    }
    if (p === "samen") {
      const pr2 = parenIn(w, T);
      if (!pr2.length) return { zin: "Binnen " + T.naam + " delen de onderwerpen weinig skills.", klein };
      const a2 = pr2[0]!;
      return {
        zin: a2[0].naam + " en " + a2[1].naam + " horen het meest bij elkaar: " + a2[2] + " skills gemeenschappelijk.",
        lijst: pr2.map((q) => ({ nr: null, naam: q[0].naam + " + " + q[1].naam, waarde: q[2] + " skills", actie: { soort: "bouw", ids: [q[0].i, q[1].i] } })),
        knop: { tekst: "Bouw hierop", actie: { soort: "bouw", ids: [a2[0].i, a2[1].i] } },
      };
    }
    if (p === "gat") {
      const g3 = gatIn(w, ows);
      if (!g3) return { zin: "Binnen " + T.naam + " is er geen gat: de grote onderwerpen delen allemaal skills." };
      return { zin: g3[0].naam + " en " + g3[1].naam + " zijn allebei groot, maar delen bijna geen skills (minder dan 3).", rest: "Weet iemand wat ze verbindt? Dat is een vraag voor de chat.", knop: { tekst: "Stel de vraag in de chat", actie: { soort: "chat", vraag: "Wat verbindt " + g3[0].naam + " en " + g3[1].naam + "?" } } };
    }
    const gr2 = ows.slice().sort((a, b) => b.docs - a.docs)[0]!;
    return { zin: "Je bent in " + T.naam + ": " + ows.length + " onderwerpen.", rest: "Klik er een om de skills te zien die daar het vaakst terugkomen.", klein, knop: { tekst: "Zoom in op " + gr2.naam, actie: { soort: "onderwerp", i: gr2.i } } };
  }
  const o = w.onderwerpen[f.o ?? 0]!;
  if (p === "wie" && ctx.sel !== null) {
    const k = ctx.kaartje;
    if (!k) return { zin: "Ik zoek op wie dit zegt…" };
    const pubs = k.pubs.map((x) => x[0]);
    return {
      zin: k.docs + " rapporten zeggen dit" + (pubs.length > 1 ? "; " + pubs[0] + " en " + pubs[1] + " het vaakst." : pubs.length ? "; " + pubs[0] + " het vaakst." : "."),
      rest: k.citaat ? "De sterkste zin staat op het kaartje (" + k.citaat.uitgever + ", " + datumLang(k.citaat.datum) + ")." : undefined,
      knop: { tekst: "Kan ik hier een opleiding op bouwen?", actie: { soort: "bouw", ids: partners(w, o.i) } },
    };
  }
  if (p === "nieuw") {
    const ns = nieuwSkills(w, o);
    if (!ns.length) return { zin: "In " + o.naam + " is bij de skills weinig nieuw." };
    const n1 = ns[0]!;
    return {
      zin: "Het nieuwst in " + o.naam + ": ‘" + n1.naam + "’.", rest: n1.nw + " van de " + n1.n + " ontwikkelingen die erom vragen, zijn van het laatste jaar.",
      lijst: ns.map((x, j) => ({ nr: j + 1, naam: x.naam, waarde: x.nw + " van " + x.n, actie: { soort: "skill", i: x.i } })), knop: { tekst: "Bekijk nummer 1", actie: { soort: "skill", i: n1.i } },
    };
  }
  const st = studentTop(w, o, ctx.alleenBevestigd), s1 = st.top[0];
  if (!s1) return { zin: "Voor " + o.naam + " zijn er (nog) geen skills" + (ctx.alleenBevestigd ? " met een bevestigde ontwikkeling." : ".") };
  const n1 = ctx.alleenBevestigd ? s1.nb : s1.n;
  const extra = st.gelijk.length ? ["Daarna " + st.gelijk.length + (st.alles.length >= 12 && st.gelijk.includes(st.alles[st.alles.length - 1]!) ? " of meer" : "") + " skills met " + st.n5 + "."] : [];
  if (ctx.alleenBevestigd) extra.push("Je telt nu alleen bevestigde ontwikkelingen.");
  return {
    zin: "Dit zijn de skills die in " + o.naam + " het vaakst terugkomen.",
    rest: "Groot = gevraagd door veel ontwikkelingen. Nummer 1 wordt gevraagd door " + n1 + " van de " + (ctx.alleenBevestigd ? o.bevestigd + " bevestigde" : o.trends) + " ontwikkelingen" + (ctx.alleenBevestigd ? "." : "; " + daarvan(s1.nw, s1.n) + " van het laatste jaar."),
    lijst: st.top.map((x, j) => ({ nr: j + 1, naam: x.naam, waarde: String(ctx.alleenBevestigd ? x.nb : x.n), actie: { soort: "skill", i: x.i } })), klein: extra,
    knop: { tekst: "Bekijk nummer 1", actie: { soort: "skill", i: s1.i } },
  };
}

export function legendaRegels(w: Wereld, f: Positie, bouw: boolean): string[] {
  if (bouw) return ["Elke cirkel is een onderwerp; groot = veel skills.", "Waar cirkels overlappen, staan de skills die ze delen.", "Het getal in elk vlak is het aantal skills."];
  const [, veel] = eenheid(w);
  if (f.s === 1) return ["Elke bol is een gebied in " + sectorNaam(w.sector) + ".", "Groot = veel " + veel + ".", "Dikke rand = veel nieuw het laatste jaar.", "Kleur = het gebied; die blijft als je inzoomt.", "Scroll of knijp om in te zoomen; klik een bol om erin te gaan."];
  if (f.s === 2) return ["Elke bol is een onderwerp van " + w.gebieden[f.t]!.naam + ".", "Groot = veel " + veel + ".", "Een stippelrand = een opkomend onderwerp, nog niet vast.", "De vage bollen aan de rand zijn de buren.", "Klik een onderwerp voor zijn skills; ‘← terug’ gaat één stand terug."];
  return ["Elke bol is een skill van " + (f.o !== null ? w.onderwerpen[f.o]!.naam : "dit onderwerp") + ".", "Groot = gevraagd door veel ontwikkelingen.", "Een groene ring = gevraagd door minstens één bevestigde ontwikkeling.", "Klik een skill voor zijn kaartje: wie vraagt het, wie zegt het."];
}

// ── Bouwen: overlappende cirkels en stoplichten ────────────────────────────

export type Venn = {
  ids: number[]; perMask: Map<number, number>; uni: number; twee: number; alle: number;
  gedeeld: [string, number][]; docs: number; rec: number; pubs: number; am: number; maten: number[];
};
export function vennData(w: Wereld, ids: number[], rap: GraafRapportenResponse | null): Venn {
  const sets = ids.map((i) => w.onderwerpen[i]!.skillSet);
  const uni = new Set<string>(); sets.forEach((s) => s.forEach((x) => uni.add(x)));
  const perMask = new Map<number, number>(); let twee = 0, alle = 0; const gd: [string, number][] = [];
  for (const x of uni) {
    let m = 0, c = 0; sets.forEach((s, j) => { if (s.has(x)) { m |= 1 << j; c++; } });
    perMask.set(m, (perMask.get(m) ?? 0) + 1);
    if (c >= 2) { twee++; gd.push([x, c]); }
    if (sets.length > 1 && c === sets.length) alle++;
  }
  const dset = new Set<number>(); ids.forEach((i) => w.onderwerpen[i]!.docSet.forEach((d) => dset.add(d)));
  let rec = 0, am = 0; const pubs = new Set<number>();
  for (const d of dset) { const r = rap?.rapporten[d]; if (!r) continue; const m = maandNr(r.maand); if (m !== null && m >= w.nu - 5 && m <= w.nu) rec++; pubs.add(r.u); am += r.am; }
  const n = (id: string) => w.vragers.get(id)?.length ?? 0;
  gd.sort((a, b) => b[1] - a[1] || n(b[0]) - n(a[0]));
  return { ids, perMask, uni: uni.size, twee, alle, gedeeld: gd, docs: dset.size, rec, pubs: pubs.size, am, maten: sets.map((s) => s.size) };
}
export type Licht = { band: "groen" | "oranje" | "rood" | "grijs"; label: string; tekst: string };
/** De stoplichten van W6: samenhang ≥ 20%; actueel ≥ 1,1× het veld (oranje 0,8–1,1); breed ≥ 20 uitgevers; kwalificatiedossier grijs. */
export function lichten(w: Wereld, v: Venn): Licht[] {
  const sam = v.twee / Math.max(v.uni, 1);
  const uit: Licht[] = [{ band: sam >= 0.2 ? "groen" : sam >= 0.1 ? "oranje" : "rood", label: "Hangt samen", tekst: v.twee + " van de " + v.uni + " skills zitten in twee of meer." }];
  if (w.metRapporten) {
    const sv6 = w.veld.rec / Math.max(w.veld.docs, 1), eigen = v.rec / Math.max(v.docs, 1), rat = sv6 ? eigen / sv6 : 0;
    uit.push({ band: rat >= 1.1 ? "groen" : rat >= 0.8 ? "oranje" : "rood", label: "Is actueel", tekst: v.rec + " van de " + v.docs + " rapporten zijn van het laatste half jaar (" + pct(eigen) + "); in het hele veld is dat " + pct(sv6) + "." });
    uit.push({ band: v.pubs >= 20 ? "groen" : v.pubs >= 8 ? "oranje" : "rood", label: "Is breed", tekst: v.pubs + " uitgevers en " + v.am + " arbeidsmarktsignalen." });
  } else {
    uit.push({ band: "grijs", label: "Is actueel", tekst: "Nog niet te zeggen: de rapporten zijn niet geladen." });
  }
  uit.push({ band: "grijs", label: "Past bij een kwalificatiedossier", tekst: "Nog niet te zeggen: de skills zijn nog niet aan kwalificatiedossiers gekoppeld." });
  return uit;
}

// ── het kaartje ────────────────────────────────────────────────────────────

export type KaartjeCijfers = {
  trends: number; nieuw: number; bevestigd: number; docs: number; uitg: number; pubs: [string, number][];
  lijkt: [number, string] | null; van: string | null; tot: string | null;
  citaat: {
    uitgever: string; datum: string | null; zin: string; alineaId: string | null; documentId: string;
    /** Besluit K2 (reparatie 06-10): het document staat achter een betaalmuur. */
    achterBetaalmuur?: boolean; betaalmuurLabel?: string;
  } | null;
};
/** Het kaartje (I1): gevraagd door (de brug op de kaart), staat in en wie zegt dat
 *  (/api/graaf/skillkaart: de rapporten achter de ontwikkelingen die de skill vragen),
 *  lijkt op (breder/smaller op de kaart), sinds, en de sterkste zin. */
export function kaartjeCijfers(w: Wereld, s: SkillBol, k: GraafSkillkaartResponse | null): KaartjeCijfers {
  const ts = [...new Set(w.vragers.get(s.id) ?? [])];
  const isNw = (id: string) => { const e = maandNr(w.punt.get(id)?.eersteBewijs); return e !== null && e >= w.nu - 11; };
  const lk = (w.lijkt.get(s.id) ?? []).map((id) => w.punt.get(id)).filter((p): p is GraafPunt => !!p && p.naam !== s.naam)
    .sort((a, b) => (b.bronnen ?? 0) - (a.bronnen ?? 0) || a.naam.localeCompare(b.naam));
  const p = w.punt.get(s.id);
  return {
    trends: ts.length, nieuw: ts.filter(isNw).length, bevestigd: ts.filter((id) => isBevestigd(w.punt.get(id))).length,
    docs: k?.rapporten ?? 0, uitg: k?.uitgevers ?? 0, pubs: (k?.wie ?? []).map((x) => [x.uitgever, x.rapporten] as [string, number]),
    lijkt: lk.length ? [lk.length, lk[0]!.naam] : null,
    van: k?.van ?? p?.eersteBewijs ?? null, tot: k?.tot ?? p?.laatsteBewijs ?? null,
    citaat: k?.zin
      ? {
          uitgever: k.zin.uitgever, datum: k.zin.datum ?? null, zin: k.zin.tekst, alineaId: k.zin.alineaId ?? null, documentId: k.zin.documentId,
          ...(k.zin.achterBetaalmuur ? { achterBetaalmuur: true, betaalmuurLabel: k.zin.betaalmuurLabel ?? "achter een betaalmuur" } : {}),
        }
      : null,
  };
}

export { maandTekst };
